import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import SiteLayout from "@/components/layout/SiteLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { LanguageSync } from "@/i18n/LanguageSync";
import { SUPPORTED_LANG_CODES, DEFAULT_LANG } from "@/i18n";
import Home from "@/pages/Home";
import SafeToSpend from "@/pages/SafeToSpend";
import Ember from "@/pages/Ember";
import Apis from "@/pages/Apis";
import DeveloperDocs from "@/pages/DeveloperDocs";
import ApiReference from "@/pages/ApiReference";
import ApiStatus from "@/pages/ApiStatus";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import LegalTerms from "@/pages/legal/Terms";
import LegalPrivacy from "@/pages/legal/Privacy";
import LegalSecurity from "@/pages/legal/Security";
import LegalCookies from "@/pages/legal/Cookies";
import LegalAUP from "@/pages/legal/AcceptableUse";
import LegalApiTerms from "@/pages/legal/ApiTerms";
import LegalDPA from "@/pages/legal/DPA";
import LegalSla from "@/pages/legal/Sla";
import Changelog from "@/pages/Changelog";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Press from "@/pages/Press";
import DeleteAccount from "@/pages/DeleteAccount";
import ForEmployers from "@/pages/ForEmployers";
import ForEmployersInquiry from "@/pages/ForEmployersInquiry";
import ForEmployersThankYou from "@/pages/ForEmployersThankYou";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalLayout from "@/components/portal/PortalLayout";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalUsers from "@/pages/portal/PortalUsers";
import PortalWebhooks from "@/pages/portal/PortalWebhooks";
import PortalAlerts from "@/pages/portal/PortalAlerts";
import PortalKeys from "@/pages/portal/PortalKeys";
import PortalReports from "@/pages/portal/PortalReports";
import PortalSettings from "@/pages/portal/PortalSettings";

/**
 * The full localized route tree. Rendered twice by <App />: once under a
 * `/:lang/*` prefix and once under `/*` so English URLs stay canonical
 * (no `/en` in the path — matches Stripe, Vercel, and Linear conventions).
 * <LangGate /> validates the `:lang` param on entry and hard-redirects
 * unknown values so a typo like `/xx/about` cleanly 404s.
 */
function SiteRoutes() {
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="safe-to-spend" element={<SafeToSpend />} />
        <Route path="ember" element={<Ember />} />
        <Route path="platform" element={<Apis />} />
        <Route path="developers" element={<DeveloperDocs />} />
        <Route path="developers/reference" element={<ApiReference />} />
        <Route path="status" element={<ApiStatus />} />
        <Route path="about" element={<About />} />
        <Route path="contact" element={<Contact />} />
        <Route path="delete-account" element={<DeleteAccount />} />
        <Route path="for-employers" element={<ForEmployers />} />
        <Route path="for-employers/inquiry" element={<ForEmployersInquiry />} />
        <Route path="for-employers/thank-you" element={<ForEmployersThankYou />} />
        <Route path="for-employers/thanks" element={<Navigate to="/for-employers/thank-you" replace />} />
        <Route path="legal/terms" element={<LegalTerms />} />
        <Route path="legal/privacy" element={<LegalPrivacy />} />
        <Route path="legal/security" element={<LegalSecurity />} />
        <Route path="legal/cookies" element={<LegalCookies />} />
        <Route path="legal/acceptable-use" element={<LegalAUP />} />
        <Route path="legal/api-terms" element={<LegalApiTerms />} />
        <Route path="legal/dpa" element={<LegalDPA />} />
        <Route path="sla" element={<LegalSla />} />
        <Route path="legal/sla" element={<Navigate to="/sla" replace />} />
        <Route path="changelog" element={<Changelog />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogPost />} />
        <Route path="press" element={<Press />} />
        <Route path="partners" element={<Navigate to="/portal/login" replace />} />
        <Route path="partners/dashboard" element={<Navigate to="/portal" replace />} />
        <Route path="admin" element={<AdminLogin />} />
        <Route
          path="admin/dashboard"
          element={
            <ProtectedRoute role="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** Wraps the site route tree with i18n syncing. The route pattern
 *  (either `/{lang}/*` or `/*`) already restricts matching to known
 *  locales, so no runtime validation is needed here. */
function LangGate() {
  return (
    <LanguageSync>
      <SiteRoutes />
    </LanguageSync>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-right" theme="light" richColors />
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Partner Portal — always English UI, no locale prefix. */}
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<PortalDashboard />} />
                <Route path="users" element={<PortalUsers />} />
                <Route path="webhooks" element={<PortalWebhooks />} />
                <Route path="usage-alerts" element={<PortalAlerts />} />
                <Route path="keys" element={<PortalKeys />} />
                <Route path="reports" element={<PortalReports />} />
                <Route path="settings" element={<PortalSettings />} />
              </Route>

              {/* Localized public site — one explicit mount per supported
                  non-default locale so /es/about resolves under LangGate
                  while /about still falls through to the English root
                  route below. Using a generic `/:lang/*` here would
                  outrank `/*` in React Router's specificity scoring and
                  hijack every non-locale URL (e.g. /about → lang="about"). */}
              {SUPPORTED_LANG_CODES
                .filter((code) => code !== DEFAULT_LANG)
                .map((code) => (
                  <Route
                    key={code}
                    path={`/${code}/*`}
                    element={<LangGate />}
                  />
                ))}
              <Route path="/*" element={<LangGate />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
